export const NCRM_LEAD_ENTITY_SCOPES = Object.freeze([
  "company_only",
  "person_only",
  "both",
]);

export const NCRM_LEAD_STAGE_TYPES = Object.freeze([
  "open",
  "conversion",
  "closed",
]);

export const NCRM_LEAD_ENTRY_STATUSES = Object.freeze([
  "open",
  "converted",
  "failed",
  "disqualified",
  "no_response",
]);

export const NCRM_LEAD_ENTRY_PRIORITIES = Object.freeze(["low", "normal", "high", "urgent"]);
export const NCRM_LEAD_ENTRY_DEFAULT_PRIORITY = "normal";

export const NCRM_LEAD_DEFAULT_STAGES = Object.freeze([
  Object.freeze({ name: "New", stage_key: "new", stage_type: "open", is_default: true }),
  Object.freeze({ name: "To be called", stage_key: "to_be_called", stage_type: "open", is_default: false }),
  Object.freeze({ name: "Interested", stage_key: "interested", stage_type: "open", is_default: false }),
  Object.freeze({
    name: "Follow-up scheduled",
    stage_key: "follow_up_scheduled",
    stage_type: "open",
    is_default: false,
  }),
  Object.freeze({
    name: "Qualified for Deal",
    stage_key: "qualified_for_deal",
    stage_type: "conversion",
    is_default: false,
  }),
  Object.freeze({ name: "Failed", stage_key: "failed", stage_type: "closed", is_default: false }),
]);

export const normalizeNcrmLeadString = (value) => String(value ?? "").trim();

export const normalizeNcrmLeadEntityScope = (value, fallback = "both") => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  if (!normalized) return NCRM_LEAD_ENTITY_SCOPES.includes(fallback) ? fallback : "both";
  return NCRM_LEAD_ENTITY_SCOPES.includes(normalized) ? normalized : null;
};

export const normalizeNcrmLeadStageType = (value, fallback = "open") => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  if (!normalized) return NCRM_LEAD_STAGE_TYPES.includes(fallback) ? fallback : "open";
  return NCRM_LEAD_STAGE_TYPES.includes(normalized) ? normalized : null;
};

export const normalizeNcrmLeadEntryStatus = (value, fallback = "open") => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  if (!normalized) return NCRM_LEAD_ENTRY_STATUSES.includes(fallback) ? fallback : "open";
  return NCRM_LEAD_ENTRY_STATUSES.includes(normalized) ? normalized : null;
};

export const normalizeNcrmLeadEntryEntityType = (value) => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  if (normalized === "company" || normalized === "person") return normalized;
  return null;
};

export const normalizeNcrmLeadEntryPriority = (value, fallback = NCRM_LEAD_ENTRY_DEFAULT_PRIORITY) => {
  const normalized = normalizeNcrmLeadString(value).toLowerCase();
  const safeFallback = NCRM_LEAD_ENTRY_PRIORITIES.includes(fallback)
    ? fallback
    : NCRM_LEAD_ENTRY_DEFAULT_PRIORITY;
  if (!normalized) return safeFallback;
  return NCRM_LEAD_ENTRY_PRIORITIES.includes(normalized) ? normalized : safeFallback;
};

// Vuetify theme color per priority. urgent=error, high=warning, low/normal=neutral.
export const resolveNcrmLeadEntryPriorityColor = (value) => {
  const normalized = normalizeNcrmLeadEntryPriority(value);
  if (normalized === "urgent") return "error";
  if (normalized === "high") return "warning";
  return "neutral";
};

// Whether to surface a priority badge on a lead card: only deliberate, non-default
// priorities (low/high/urgent) get a chip so default "normal" cards stay clean.
export const shouldShowNcrmLeadEntryPriority = (value) =>
  normalizeNcrmLeadEntryPriority(value) !== NCRM_LEAD_ENTRY_DEFAULT_PRIORITY;

export const normalizeNcrmLeadStageKey = (value) => {
  const normalized = normalizeNcrmLeadString(value);
  if (!normalized) return "";

  const ascii = normalized
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return ascii
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_")
    .slice(0, 120);
};

export const inferNcrmLeadStageTypeFromKey = (stageKey) => {
  const normalized = normalizeNcrmLeadString(stageKey).toLowerCase();
  if (normalized === "qualified_for_deal") return "conversion";
  if (normalized === "failed") return "closed";
  return "open";
};

export const cloneNcrmLeadDefaultStages = () =>
  NCRM_LEAD_DEFAULT_STAGES.map((stage) => ({ ...stage }));

export const resolveNcrmLeadEntryDisplayName = (entry = {}) => {
  const entityType = normalizeNcrmLeadEntryEntityType(entry.entity_type ?? entry.entityType);
  if (entityType === "company") {
    return normalizeNcrmLeadString(entry.company_name ?? entry.companyName) || "-";
  }
  if (entityType === "person") {
    return normalizeNcrmLeadString(entry.person_name ?? entry.personName) || "-";
  }
  return "-";
};

export const resolveNcrmLeadEntrySourceRoute = (entry = {}) => {
  const entityType = normalizeNcrmLeadEntryEntityType(entry.entity_type ?? entry.entityType);
  const companyId = normalizeNcrmLeadString(entry.ncrm_company_id ?? entry.ncrmCompanyId);
  const personId = normalizeNcrmLeadString(entry.ncrm_person_id ?? entry.ncrmPersonId);

  if (entityType === "company" && companyId) {
    return {
      name: "NcrmCompanyDetails",
      params: { id: companyId },
      query: { tab: "activity" },
    };
  }

  if (entityType === "person" && personId) {
    return {
      name: "NcrmPersonDetails",
      params: { id: personId },
      query: { tab: "activity" },
    };
  }

  return null;
};

export const formatNcrmLeadDateTime = (value) => {
  const normalized = normalizeNcrmLeadString(value);
  if (!normalized) return "-";
  const parsed = new Date(normalized.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return normalized;
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = parsed.getFullYear();
  const HH = String(parsed.getHours()).padStart(2, "0");
  const min = String(parsed.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy}, ${HH}:${min}`;
};
